import {
  DropboxBusyStatuses,
  DropboxStaleSeconds,
  ImportSources,
  ImportStatuses,
  ImportUploadableStatuses
} from '../../../constants/importStatuses';

export const formatDateTime = value => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export const formatDuration = seconds => {
  if (!seconds || seconds <= 0) {
    return '0s';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = Math.round(seconds % 60);

  return [hours && `${hours}h`, minutes && `${minutes}m`, (!hours && rest) && `${rest}s`].filter(Boolean).join(' ') || '0s';
};

export const secondsSince = value => (value ? Math.max(0, (Date.now() - new Date(value).getTime()) / 1000) : 0);

export const formatMoney = value => (value === null || value === undefined ? '—' : `$${Number(value).toFixed(2)}`);

export const isDropboxBusy = importData => DropboxBusyStatuses.includes(importData?.summary?.dropbox?.status);

/**
 * Seconds since a busy pull last reported, from its heartbeat or, for a pull
 * that never wrote one, from when it started or was queued.
 */
export const dropboxSilentSeconds = dropbox => secondsSince(dropbox?.heartbeatAt ?? dropbox?.startedAt ?? dropbox?.queuedAt);

export const isDropboxStale = importData => isDropboxBusy(importData) && dropboxSilentSeconds(importData.summary.dropbox) > DropboxStaleSeconds;

export const acceptsUploads = importData => ImportUploadableStatuses.includes(importData?.status) && !isDropboxBusy(importData);

export const hasManifest = importData => Boolean(importData?.manifestS3Key);

/**
 * Which actions make sense for an import in its current state. Mirrors the
 * API's guards so the buttons never offer a request that would 422.
 */
export const availableActions = importData => {
  if (!importData) {
    return {};
  }

  const { status, source } = importData;
  const rowErrors = importData.rowCounts?.error ?? 0;
  const total = importData.rowCounts?.total ?? 0;

  return {
    stageFromDropbox: source === ImportSources.dropbox && acceptsUploads(importData),
    cancelPull: source === ImportSources.dropbox && isDropboxBusy(importData),
    validate: [ImportStatuses.created, ImportStatuses.validated, ImportStatuses.completed, ImportStatuses.failed, ImportStatuses.rolledBack].includes(status)
      && hasManifest(importData)
      && !isDropboxBusy(importData),
    run: status === ImportStatuses.validated && total - rowErrors > 0,
    pause: status === ImportStatuses.running,
    resume: status === ImportStatuses.paused,
    rollback: [ImportStatuses.completed, ImportStatuses.failed].includes(status),
    report: total > 0
  };
};

export const needsRevalidation = importData => importData?.status === ImportStatuses.created && (importData?.rowCounts?.total ?? 0) > 0;

export const WizardSteps = { files: 1, manifest: 2, review: 3, preview: 4, run: 5 };

const RUN_STATUSES = [ImportStatuses.running, ImportStatuses.paused, ImportStatuses.completed, ImportStatuses.failed, ImportStatuses.rolledBack];

/**
 * The last wizard step the import has earned: files staged unlock the manifest,
 * a dry run unlocks the review, a clean dry run the preview, and a run the
 * progress step. Earlier steps stay open for changes.
 */
export const furthestStep = (importData, stagedTotal = 0) => {
  const summary = importData?.summary ?? {};

  if (summary.run && RUN_STATUSES.includes(importData?.status)) {
    return WizardSteps.run;
  }

  if (importData?.status === ImportStatuses.validated) {
    return WizardSteps.preview;
  }

  if (summary.rows || (importData?.rowCounts?.total ?? 0) > 0) {
    return WizardSteps.review;
  }

  if (stagedTotal > 0 || hasManifest(importData) || isDropboxBusy(importData)) {
    return WizardSteps.manifest;
  }

  return WizardSteps.files;
};

/**
 * Where the wizard opens: the furthest step, except that a clean dry run lands
 * on the review so the preview is a deliberate next click.
 */
export const defaultStep = (importData, stagedTotal = 0) => {
  const furthest = furthestStep(importData, stagedTotal);

  return furthest === WizardSteps.preview ? WizardSteps.review : furthest;
};

const effectiveValue = (row, field) => row?.overrides?.[field] ?? row?.data?.[field] ?? null;

/**
 * The rows arranged the way the run will create things: projects (under their
 * department), the records inside each (one per record key, named after the
 * insured) and the documents on each record. Rows with errors are skipped by
 * the run, so they are counted rather than placed.
 */
export const buildPreviewTree = (rows, fallbackProjectName) => {
  const projects = new Map();
  let skipped = 0;

  (rows ?? []).forEach(row => {
    if (row.status === 'error') {
      skipped += 1;
      return;
    }

    const resolved = row.resolved ?? {};
    const projectName = resolved.projectName || `${fallbackProjectName || 'Import'} (default)`;
    const departmentName = resolved.departmentName || 'Imports';
    const projectKey = `${departmentName}|${projectName}`.toLowerCase();

    if (!projects.has(projectKey)) {
      projects.set(projectKey, { key: projectKey, name: projectName, department: departmentName, existing: Boolean(row.jobId), records: new Map() });
    }

    const project = projects.get(projectKey);
    const recordKey = row.recordKey || `row-${row.id}`;

    if (!project.records.has(recordKey)) {
      project.records.set(recordKey, {
        key: recordKey,
        insuredName: resolved.insuredName || effectiveValue(row, 'insuredName') || '(unknown insured)',
        insuredMatch: resolved.insuredMatch ?? null,
        insuredId: row.insuredId ?? null,
        status: resolved.record?.status ?? 'active',
        templateSource: resolved.record?.templateSource ?? null,
        documents: []
      });
    }

    project.records.get(recordKey).documents.push({
      id: row.id,
      path: row.filePath,
      fileType: resolved.fileType ?? effectiveValue(row, 'fileType'),
      fileStatus: resolved.fileStatus ?? 'active',
      rowStatus: row.status,
      issues: row.issues ?? []
    });
  });

  const byName = (a, b) => a.localeCompare(b);
  const list = [...projects.values()]
    .map(project => ({ ...project, records: [...project.records.values()].sort((a, b) => byName(a.insuredName, b.insuredName)) }))
    .sort((a, b) => byName(a.department, b.department) || byName(a.name, b.name));

  return {
    projects: list,
    skipped,
    recordCount: list.reduce((sum, project) => sum + project.records.length, 0),
    documentCount: list.reduce((sum, project) => sum + project.records.reduce((inner, record) => inner + record.documents.length, 0), 0)
  };
};

const SCAN_PHASES = ['contracts', 'policies', 'questions'];

/**
 * The run's progress with the AI scans counted alongside the rows, since the
 * scans are most of the wall clock. Scans are queued as the row phases reach
 * them, so until the last scan phase is done the dry run's estimate stands in
 * for the ones not queued yet.
 */
export const runProgress = importData => {
  const counts = importData?.rowCounts ?? {};
  const run = importData?.summary?.run ?? {};
  const ai = importData?.summary?.ai ?? {};
  const phases = run.phasesCompleted ?? [];

  const rowsDone = (counts.completed ?? 0) + (counts.failed ?? 0) + (counts.skipped ?? 0);
  const rowsTotal = counts.total ?? 0;

  const dispatched = run.scansDispatched ?? 0;
  const pending = importData?.pendingScans ?? 0;
  const scansDone = Math.max(0, dispatched - pending);
  const estimated = (ai.coiScans ?? 0) + (ai.contractExtractions ?? 0) + (ai.questionScans ?? 0);
  const allQueued = importData?.status === ImportStatuses.completed || SCAN_PHASES.every(phase => phases.includes(phase));
  const scansTotal = allQueued ? dispatched : Math.max(dispatched, estimated);

  const total = rowsTotal + scansTotal;
  const percent = total > 0 ? Math.round(((rowsDone + scansDone) / total) * 100) : 0;

  return { rowsDone, rowsTotal, scansDone, scansTotal, pending, percent };
};

/**
 * The issues on a row, worst first.
 */
export const sortedIssues = row => [...(row?.issues ?? [])].sort((a, b) => (a.level === 'error' ? -1 : 0) - (b.level === 'error' ? -1 : 0));

export const humanize = value => (value ? String(value).replace(/_/g, ' ') : '');
